import { apiClient, ApiError } from "@/services/api.client"
import { uploadCaseDocument } from "@/services/storage-upload"
import { putJson } from "./oia-http"
import type {
  OiaAddAuthorityRequest,
  OiaAddAuthorityResponse,
  OiaAddIssueRequest,
  OiaAddIssueResponse,
  OiaAppealMeta,
  OiaAppealMetaResponse,
  OiaPutOrderWordingRequest,
  OiaPutOrderWordingResponse,
  OiaAuthoritiesResponse,
  OiaAuthorityActionRequest,
  OiaAuthorityActionResponse,
  OiaCaseView,
  OiaDecisionsResponse,
  OiaIdentifyRequest,
  OiaIdentifyResponse,
  OiaNextStepResolveRequest,
  OiaNextStepResolveResponse,
  OiaPutCaseViewResponse,
  OiaTemplatesResponse,
} from "../types"

/**
 * Thin typed client for the OIA REST surface (backend oia/schemas/api.py),
 * proxied and ownership-checked by the gateway under /api/v1/oia/*.
 *
 * Every call goes through `apiClient` (the orchestrator, with the officer's
 * JWT) — never the raw sidecar fetch the OIO rewrite stream uses, which sends
 * no Authorization header (frontend map G7). Officer writes (card
 * resolutions, authority actions, the case view) carry the officer's
 * identity, so they must go through the authenticated gateway.
 *
 * No endpoint here starts a run. A write that changes a decision input answers
 * `revise_required`; the caller then marks a new attempt and starts a revise
 * (hooks/useOiaRuns). While a run is live every write answers 409.
 */
const BASE = "/api/v1/oia"

function caseUrl(threadId: string): string {
  // The backend refuses empty and placeholder ids ("unknown", "undefined")
  // because a lost id once made every case share one blob; refuse them here
  // too, before a request can go out.
  const id = threadId.trim()
  if (!id || ["unknown", "undefined", "null", "none"].includes(id.toLowerCase())) {
    throw new Error("No case is open: refusing to call the appeal service without a case id.")
  }
  return `${BASE}/cases/${encodeURIComponent(id)}`
}

/** 404 = the case has no OIA state yet (nothing uploaded or run). */
export function isNotFound(error: unknown): boolean {
  return (
    (error instanceof ApiError && error.status === 404) ||
    // apiClient keeps the status only for structured error bodies; a plain
    // FastAPI 404 arrives as Error("Not Found") or Error("HTTP 404").
    (error instanceof Error && /\b404\b|not found/i.test(error.message))
  )
}

/** 409 = a draft or revise run is live; writes wait for it to finish. */
export function isLiveRunConflict(error: unknown): boolean {
  return (
    (error instanceof ApiError && error.status === 409) ||
    (error instanceof Error && /\b409\b|run is live/i.test(error.message))
  )
}

export const oiaService = {
  getCase: (threadId: string) => apiClient.get<OiaCaseView>(caseUrl(threadId)),

  getDecisions: (threadId: string) => apiClient.get<OiaDecisionsResponse>(`${caseUrl(threadId)}/decisions`),

  getAuthorities: (threadId: string) =>
    apiClient.get<OiaAuthoritiesResponse>(`${caseUrl(threadId)}/authorities`),

  identify: (threadId: string, body: OiaIdentifyRequest) =>
    apiClient.post<OiaIdentifyResponse>(`${caseUrl(threadId)}/identify`, body),

  putCaseView: (threadId: string, text: string) =>
    putJson<OiaPutCaseViewResponse>(`${caseUrl(threadId)}/case-view`, { text }),

  resolveCard: (threadId: string, cardId: string, body: OiaNextStepResolveRequest) =>
    apiClient.post<OiaNextStepResolveResponse>(
      `${caseUrl(threadId)}/next-steps/${encodeURIComponent(cardId)}/resolve`,
      body,
    ),

  putAppealMeta: (threadId: string, meta: OiaAppealMeta) =>
    putJson<OiaAppealMetaResponse>(`${caseUrl(threadId)}/appeal-meta`, meta),

  /** The officer's own wording edit of the order (never a decision change). */
  putOrderWording: (threadId: string, body: OiaPutOrderWordingRequest) =>
    putJson<OiaPutOrderWordingResponse>(`${caseUrl(threadId)}/order`, body),

  putAuthorityAction: (threadId: string, authorityId: string, body: OiaAuthorityActionRequest) =>
    putJson<OiaAuthorityActionResponse>(
      `${caseUrl(threadId)}/authorities/${encodeURIComponent(authorityId)}`,
      body,
    ),

  addAuthority: (threadId: string, body: OiaAddAuthorityRequest) =>
    apiClient.post<OiaAddAuthorityResponse>(`${caseUrl(threadId)}/authorities`, body),

  addIssue: (threadId: string, body: OiaAddIssueRequest) =>
    apiClient.post<OiaAddIssueResponse>(`${caseUrl(threadId)}/issues`, body),

  getTemplates: () => apiClient.get<OiaTemplatesResponse>(`${BASE}/templates`),

  /**
   * Upload one file of the appeal to storage (the same endpoint the OIO intake
   * uses) and return its blob url, which becomes an OiaDocumentRef.url.
   */
  uploadFile: async (file: File, userId: string): Promise<string> => {
    return uploadCaseDocument(file, userId || "anonymous")
  },
}

export type OiaService = typeof oiaService
